import React, { useMemo, useState } from 'react';
import { Customer } from '../../types';
import {
    createCustomer,
    fetchCustomers,
    linkProjectsToCustomer,
    updateCustomer,
} from '../../services/crmService';
import { loadProjectsFromCloud } from '../../services/cloudService';
import { appendChangeEntry, diffCustomerChanges } from '../../services/customerChangeLog';
import { supabase } from '../../services/supabaseClient';
import { fileToTable } from '../../services/customerListFile';
import {
    ColumnMapping,
    ImportColumnKey,
    ImportedCustomerRow,
    autoMapColumns,
    extfTableToImportRows,
    isDatevExtfTable,
    rowsToImportRows,
} from '../../services/customerListImport';
import {
    NumberPreview,
    OnboardingAction,
    OnboardingResult,
    OnboardingRow,
    ProjectCompanyCandidate,
    buildOnboardingPlan,
    executeOnboardingPlan,
    extractProjectCompanies,
    planProjectLinks,
    previewCustomerNumber,
    summarizePlan,
} from '../../services/customerOnboarding';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import {
    AlertTriangle, ArrowRight, Building2, Check, CheckCircle2, FileSpreadsheet, GitMerge,
    Link2, Loader2, Upload, Users, X,
} from 'lucide-react';

/**
 * Kunden-Uebernahme: die Unternehmen aus den Projekten UND die Kundenliste der
 * Steuerkanzlei in EINEM Abgleich, bevor irgendetwas angelegt wird.
 *
 * Duenne Huelle — Abgleich, Nummernvergabe-Vorschau, Verknuepfungsplan und
 * Ausfuehrung liegen als pure Funktionen in services/customerOnboarding.ts
 * (Standing Decision: Logik testbar ohne DOM). Hier stehen nur Dateiauswahl,
 * Tabelle und die Verdrahtung mit Supabase.
 */
interface Props {
    onClose: () => void;
    onImported: () => void;
}

type Step = 'sources' | 'mapping' | 'loading' | 'review' | 'result' | 'error';

const FIELD_LABELS: Record<ImportColumnKey, string> = {
    customerNumber: 'Kundennummer',
    name: 'Name / Firma *',
    firstName: 'Vorname (Privatperson)',
    lastName: 'Nachname (Privatperson)',
    street: 'Straße',
    zip: 'PLZ',
    city: 'Ort',
    email: 'E-Mail',
    phone: 'Telefon',
    salutation: 'Anrede',
    title: 'Titel',
};

const CLASS_BADGE: Record<OnboardingRow['rowClass'], { label: string; className: string }> = {
    new: { label: 'neu', className: 'bg-blue-50 text-blue-600' },
    merged_sources: { label: 'Projekt + Liste', className: 'bg-indigo-50 text-indigo-600' },
    existing_match: { label: 'vorhanden', className: 'bg-emerald-50 text-emerald-600' },
    number_conflict: { label: 'Nummernkonflikt', className: 'bg-red-50 text-red-700' },
    ambiguous: { label: 'unklar', className: 'bg-amber-50 text-amber-700' },
    duplicate_in_source: { label: 'doppelt', className: 'bg-slate-100 text-slate-500' },
};

const numberText = (p: NumberPreview): string => {
    switch (p.kind) {
        case 'list':
            return p.number;
        case 'keep':
            return `${p.number} (bleibt)`;
        case 'auto':
            return `automatisch (${p.range.key}: ${p.range.start}–${p.range.end})`;
        default:
            return 'manuell vergeben';
    }
};

export const CustomerOnboardingWizard: React.FC<Props> = ({ onClose, onImported }) => {
    const [step, setStep] = useState<Step>('sources');
    const [error, setError] = useState<string | null>(null);

    // Quelle A: Projekte
    const [useProjects, setUseProjects] = useState(true);

    // Quelle B: Kundenliste (CSV/Excel/DATEV-EXTF)
    const [fileName, setFileName] = useState('');
    const [listRows, setListRows] = useState<ImportedCustomerRow[]>([]);
    const [extfDetected, setExtfDetected] = useState(false);
    const [headers, setHeaders] = useState<string[]>([]);
    const [tableRows, setTableRows] = useState<string[][]>([]);
    const [mapping, setMapping] = useState<ColumnMapping>({});

    // Abgleich
    const [plan, setPlan] = useState<OnboardingRow[]>([]);
    const [projectCount, setProjectCount] = useState(0);
    const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [result, setResult] = useState<OnboardingResult | null>(null);

    const links = useMemo(() => planProjectLinks(plan), [plan]);
    const summary = useMemo(() => summarizePlan(plan, links), [plan, links]);

    const fail = (msg: string) => {
        setError(msg);
        setStep('error');
    };

    // ─── Datei einlesen ──────────────────────────────────────────────────────

    const handleFile = async (file: File) => {
        setFileName(file.name);
        setError(null);
        setExtfDetected(false);
        setStep('loading');
        try {
            const table = await fileToTable(file);
            if (!table.headers.length || !table.rows.length) {
                return fail('Die Datei enthält keine erkennbare Tabelle (Kopfzeile + Datenzeilen).');
            }
            // DATEV-EXTF bringt seinen eigenen Spaltenkopf mit — die Zuordnung
            // steht fest und wird NICHT dem Nutzer vorgelegt.
            if (isDatevExtfTable(table.headers)) {
                const imported = extfTableToImportRows(table.headers, table.rows);
                if (!imported.length) return fail('Die DATEV-EXTF-Datei enthält keine Zeilen mit Namen.');
                setExtfDetected(true);
                setListRows(imported);
                setStep('sources');
                return;
            }
            setHeaders(table.headers);
            setTableRows(table.rows);
            setMapping(autoMapColumns(table.headers));
            setStep('mapping');
        } catch (e) {
            fail(rechtFehlerOder(e));
        }
    };

    const applyMapping = () => {
        if (!mapping.name && !mapping.firstName && !mapping.lastName) {
            setError('Bitte mindestens die Spalte „Name / Firma“ oder Vor-/Nachname zuordnen.');
            return;
        }
        const imported = rowsToImportRows(headers, tableRows, mapping);
        if (!imported.length) return fail('Nach der Zuordnung bleiben keine Zeilen mit Namen übrig.');
        setError(null);
        setListRows(imported);
        setStep('sources');
    };

    // ─── Abgleich aufbauen ───────────────────────────────────────────────────

    const buildPlan = async () => {
        setStep('loading');
        setError(null);
        try {
            // Archivierte MITLADEN: ein Kandidat, der einem archivierten Kunden
            // entspricht, darf keine zweite Kundennummer bekommen.
            const [existing, projects] = await Promise.all([
                fetchCustomers({ includeArchived: true }),
                useProjects ? loadProjectsFromCloud() : Promise.resolve([] as unknown[]),
            ]);
            const projectCompanies: ProjectCompanyCandidate[] = useProjects
                ? extractProjectCompanies(projects)
                : [];
            setAllCustomers(existing);
            setProjectCount(projects.length);
            setPlan(buildOnboardingPlan({ projectCompanies, listRows, existing }));
            setStep('review');
        } catch (e) {
            fail(rechtFehlerOder(e));
        }
    };

    const patchRow = (id: string, patch: Partial<OnboardingRow>) => {
        setPlan((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    // ─── Ausfuehren ──────────────────────────────────────────────────────────

    /**
     * Aktualisierung eines Bestandskunden inkl. Historie — gleiche Regel wie im
     * Listen-Import: Stammdaten-Aenderungen werden im changeLog festgehalten.
     */
    const updateWithHistory = async (id: string, updates: Partial<Customer>) => {
        const current = allCustomers.find((c) => c.id === id);
        const payload = { ...updates };
        if (current) {
            const changes = diffCustomerChanges(current, { ...current, ...updates });
            if (changes.length) {
                const { data: auth } = await supabase.auth.getUser();
                payload.changeLog = appendChangeEntry(
                    current.changeLog ?? [],
                    changes,
                    new Date().toISOString(),
                    auth.user?.email ?? undefined,
                );
            }
        }
        await updateCustomer(id, payload);
        setAllCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, ...payload } : c)));
    };

    const handleRun = async () => {
        setRunning(true);
        setError(null);
        setProgress({ done: 0, total: summary.create + summary.update });
        try {
            const res = await executeOnboardingPlan(plan, {
                createCustomer: (input) => createCustomer(input),
                updateCustomer: updateWithHistory,
                linkProjects: linkProjectsToCustomer,
                onProgress: (done, total) => setProgress({ done, total }),
            });
            // Erledigte Zeilen auf „überspringen“ setzen: geht der Nutzer nach
            // Teilfehlern zurueck in den Abgleich, darf ein zweiter Lauf NICHT
            // dieselben Kunden ein zweites Mal anlegen.
            const doneIds = new Set(
                res.rows.filter((r) => r.status !== 'failed' && r.status !== 'skipped').map((r) => r.rowId),
            );
            setPlan((prev) =>
                prev.map((r) =>
                    doneIds.has(r.id)
                        ? { ...r, action: 'skip' as const, hints: [...r.hints, 'Bereits übernommen.'] }
                        : r,
                ),
            );
            setResult(res);
            setStep('result');
            if (res.created + res.updated > 0) onImported();
        } catch (e) {
            // Sollte nicht vorkommen — executeOnboardingPlan faengt je Zeile ab.
            setError(rechtFehlerOder(e));
        } finally {
            setRunning(false);
            setProgress(null);
        }
    };

    // ─── Rendering ───────────────────────────────────────────────────────────

    const canBuild = useProjects || listRows.length > 0;
    const actionOptions = (row: OnboardingRow): { value: OnboardingAction; label: string }[] => [
        { value: 'create', label: 'Neu anlegen' },
        ...(row.existing ? [{ value: 'update' as const, label: `Mit „${row.existing.name}“ zusammenführen` }] : []),
        { value: 'skip', label: 'Überspringen' },
    ];

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><Users className="w-6 h-6 text-blue-400" /></div>
                        <div>
                            <h3 className="font-bold text-lg">Kunden übernehmen</h3>
                            <p className="text-xs text-slate-400">
                                Unternehmen aus den Projekten und die Kundenliste der Steuerkanzlei in einem Abgleich —
                                angelegt wird erst nach Ihrer Bestätigung.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={running} className="hover:bg-white/10 p-2 rounded-full transition-all disabled:opacity-40"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {/* ── Schritt 1: Quellen ── */}
                    {step === 'sources' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <label className="p-5 rounded-2xl border border-slate-200 hover:border-blue-300 cursor-pointer flex gap-3 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={useProjects}
                                        onChange={(e) => setUseProjects(e.target.checked)}
                                        className="mt-1 w-4 h-4 accent-blue-600 shrink-0"
                                    />
                                    <div>
                                        <div className="font-bold text-slate-800 flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-slate-400" /> Unternehmen aus den Projekten
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1 leading-snug">
                                            Auftraggeber <strong>und</strong> Bauherren aller Projekte werden gelesen und nach
                                            Namen gruppiert. Verknüpft wird ein Projekt nur mit seinem <strong>Auftraggeber</strong>,
                                            und nur solange es noch keinem Kunden gehört. Ein Bauherr wird als Firma angelegt,
                                            bekommt das Projekt aber nicht — er ist nicht automatisch der Rechnungsempfänger.
                                        </p>
                                    </div>
                                </label>

                                <div className="p-5 rounded-2xl border border-slate-200 space-y-2">
                                    <div className="font-bold text-slate-800 flex items-center gap-2">
                                        <FileSpreadsheet className="w-4 h-4 text-slate-400" /> Kundenliste der Steuerkanzlei
                                    </div>
                                    {listRows.length > 0 ? (
                                        <div className="text-xs text-slate-600 space-y-2">
                                            <p className="flex items-center gap-2 font-medium text-emerald-700">
                                                <CheckCircle2 className="w-4 h-4" />
                                                {listRows.length} Zeilen aus {fileName}
                                                {extfDetected ? ' (DATEV-EXTF erkannt)' : ''}
                                            </p>
                                            <Button variant="ghost" onClick={() => { setListRows([]); setFileName(''); setExtfDetected(false); }}>
                                                Datei entfernen
                                            </Button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center gap-1 h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-slate-50 transition-colors text-slate-400">
                                            <Upload className="w-6 h-6" />
                                            <span className="text-xs font-medium">CSV, Excel (.xlsx) oder DATEV-EXTF wählen</span>
                                            <span className="text-[11px]">optional — der Abgleich läuft auch nur mit den Projekten</span>
                                            <input
                                                type="file"
                                                accept=".csv,.txt,.xlsx,.xls"
                                                className="hidden"
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 rounded-xl p-4">
                                Beide Quellen werden zusammen abgeglichen: gleiche Firmen aus Projekt und Liste werden zu
                                <strong> einem</strong> Kunden, Treffer im Bestand werden ergänzt statt neu angelegt, und die
                                Kundennummer der Kanzlei hat immer Vorrang vor der automatischen Vergabe.
                                Nur ähnliche Namen werden <strong>nicht</strong> automatisch zusammengeführt — die legen Sie selbst fest.
                            </p>

                            <div className="flex justify-end gap-3">
                                <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                                <Button variant="primary" onClick={() => void buildPlan()} disabled={!canBuild} icon={<ArrowRight className="w-4 h-4" />}>
                                    Abgleichen
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ── Schritt 1b: Spalten zuordnen ── */}
                    {step === 'mapping' && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-600">
                                Spalten aus <strong>{fileName}</strong> zuordnen. Automatisch erkannte Zuordnungen bitte prüfen.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(Object.keys(FIELD_LABELS) as ImportColumnKey[]).map((key) => (
                                    <div key={key}>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">{FIELD_LABELS[key]}</label>
                                        <select
                                            value={mapping[key] ?? ''}
                                            onChange={(e) => setMapping({ ...mapping, [key]: e.target.value || undefined })}
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                                        >
                                            <option value="">— nicht zugeordnet —</option>
                                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex justify-end gap-3">
                                <Button variant="ghost" onClick={() => setStep('sources')}>Zurück</Button>
                                <Button variant="primary" onClick={applyMapping} icon={<ArrowRight className="w-4 h-4" />}>
                                    Übernehmen ({tableRows.length} Zeilen)
                                </Button>
                            </div>
                        </div>
                    )}

                    {step === 'loading' && (
                        <div className="h-52 flex flex-col items-center justify-center gap-3 text-slate-500">
                            <Loader2 className="w-8 h-8 animate-spin" />
                            <span className="text-sm">Daten werden gelesen und abgeglichen…</span>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="h-52 flex flex-col items-center justify-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-amber-500" />
                            <p className="text-sm text-slate-600 text-center max-w-md">{error}</p>
                            <Button variant="secondary" onClick={() => { setError(null); setStep('sources'); }}>Zurück</Button>
                        </div>
                    )}

                    {/* ── Schritt 2: Abgleich ── */}
                    {step === 'review' && (
                        plan.length === 0 ? (
                            <div className="py-16 text-center text-slate-400">
                                <Users className="w-10 h-10 opacity-20 mx-auto mb-3" />
                                <p className="font-bold text-slate-600">Nichts zu übernehmen</p>
                                <p className="text-xs">
                                    {useProjects ? `${projectCount} Projekte geprüft — ` : ''}
                                    keine noch nicht übertragenen Unternehmen gefunden.
                                </p>
                            </div>
                        ) : (
                            <table className="w-full text-left text-sm border border-slate-200 rounded-xl overflow-hidden">
                                <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[11px] tracking-wider">
                                    <tr>
                                        <th className="p-3">Name</th>
                                        <th className="p-3 w-[110px]">Quelle</th>
                                        <th className="p-3 w-[170px]">Kundennummer</th>
                                        <th className="p-3">Abgleich</th>
                                        <th className="p-3 w-[90px]">Projekte</th>
                                        <th className="p-3 w-[230px]">Aktion</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {plan.map((row) => {
                                        const badge = CLASS_BADGE[row.rowClass];
                                        const linked = links.byRow.get(row.id)?.length ?? 0;
                                        const showTakeNumber =
                                            row.action === 'update' &&
                                            !!row.customerNumber &&
                                            !!row.existing &&
                                            row.customerNumber !== row.existing.customerNumber;
                                        return (
                                            <tr key={row.id} className={row.action === 'skip' ? 'bg-slate-50/60 text-slate-400' : undefined}>
                                                <td className="p-3">
                                                    <div className="font-medium text-slate-800">{row.name}</div>
                                                    {row.address && <div className="text-[11px] text-slate-500">{row.address}</div>}
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {row.sources.includes('projects') && (
                                                            <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">Projekte</span>
                                                        )}
                                                        {row.sources.includes('list') && (
                                                            <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">Liste</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <span className="font-mono text-xs text-slate-700">{numberText(previewCustomerNumber(row))}</span>
                                                    {showTakeNumber && (
                                                        <label className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={row.takeNumber}
                                                                onChange={(e) => patchRow(row.id, { takeNumber: e.target.checked })}
                                                                className="mt-0.5 w-3.5 h-3.5 accent-blue-600"
                                                            />
                                                            Nummer {row.customerNumber} aus der Liste übernehmen
                                                        </label>
                                                    )}
                                                </td>
                                                <td className="p-3 align-top">
                                                    <span className={`text-[11px] font-bold uppercase rounded px-2 py-0.5 inline-block ${badge.className}`}>
                                                        {badge.label}
                                                    </span>
                                                    {row.hints.map((h, i) => (
                                                        <p key={i} className="mt-1 text-[11px] text-slate-500 leading-snug">{h}</p>
                                                    ))}
                                                </td>
                                                <td className="p-3 text-xs text-slate-500">
                                                    {row.origins.length === 0 ? '—' : (
                                                        <span
                                                            title={row.origins
                                                                .map((o) => {
                                                                    const label = o.projectNumber || o.projectId;
                                                                    if (o.role === 'bauherr') return `${label} (Bauherr – wird nicht verknüpft)`;
                                                                    return `${label} (Auftraggeber)${o.linkable ? '' : ' – bereits zugeordnet'}`;
                                                                })
                                                                .join('\n')}
                                                            className="inline-flex items-center gap-1"
                                                        >
                                                            <Link2 className="w-3 h-3 text-slate-300" />
                                                            {linked}/{row.origins.length}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    <select
                                                        value={row.action}
                                                        onChange={(e) => patchRow(row.id, { action: e.target.value as OnboardingAction })}
                                                        className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                                                    >
                                                        {actionOptions(row).map((o) => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* ── Schritt 3: Ergebnis ── */}
                    {step === 'result' && result && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                {([
                                    ['angelegt', result.created, 'text-emerald-600'],
                                    ['aktualisiert', result.updated, 'text-blue-600'],
                                    ['Projekte verknüpft', result.linked, 'text-indigo-600'],
                                    ['übersprungen', result.skipped, 'text-slate-500'],
                                    ['fehlgeschlagen', result.failed, 'text-red-600'],
                                ] as const).map(([label, value, color]) => (
                                    <div key={label} className="p-4 rounded-2xl border border-slate-200 text-center">
                                        <div className={`text-2xl font-bold ${color}`}>{value}</div>
                                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
                                    </div>
                                ))}
                            </div>
                            {result.rows.filter((r) => r.error || r.warning).length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Hinweise</h4>
                                    {result.rows.filter((r) => r.error || r.warning).map((r) => (
                                        <div
                                            key={r.rowId}
                                            className={`p-3 rounded-xl border text-xs ${r.error ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
                                        >
                                            <strong>{r.name}:</strong> {r.error ?? r.warning}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {step === 'review' && plan.length > 0 && (
                    <div className="p-5 bg-slate-50 border-t flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
                        <span className="text-xs text-slate-500">
                            {progress
                                ? `Wird übernommen: ${progress.done}/${progress.total}…`
                                : `${summary.create} anlegen · ${summary.update} zusammenführen · ${summary.skip} überspringen · ${summary.link} Projekte verknüpfen` +
                                  (summary.conflicts > 0 ? ` · ${summary.conflicts} Nummernkonflikt(e)` : '')}
                        </span>
                        <div className="flex gap-3">
                            <Button variant="ghost" onClick={onClose} disabled={running}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={() => void handleRun()}
                                disabled={running || summary.create + summary.update === 0}
                                icon={running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            >
                                Jetzt übernehmen
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'review' && error && <p className="px-6 pb-4 text-sm text-red-600 bg-slate-50">{error}</p>}

                {step === 'result' && result && (
                    <div className="p-5 bg-slate-50 border-t flex justify-between items-center gap-3 shrink-0">
                        <span className="text-xs text-slate-500 inline-flex items-center gap-2">
                            <GitMerge className="w-3.5 h-3.5" />
                            {result.failed > 0
                                ? 'Erledigte Zeilen sind abgehakt — im Abgleich lassen sich die fehlgeschlagenen anders entscheiden.'
                                : 'Alle ausgewählten Zeilen sind übernommen.'}
                        </span>
                        <div className="flex gap-3">
                            {result.failed > 0 && (
                                <Button variant="secondary" onClick={() => { setResult(null); setStep('review'); }}>
                                    Zurück zum Abgleich
                                </Button>
                            )}
                            <Button variant="primary" onClick={() => { onImported(); onClose(); }} icon={<CheckCircle2 className="w-4 h-4" />}>
                                Fertig
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
